import { useState, useCallback, useMemo } from 'react';
import { supabase, STORAGE_BUCKET, SUPABASE_CONFIGURED } from '../lib/supabase';
import { IDSubmission, PersonalInfo } from '../types';
import { toast } from 'react-hot-toast';
import { getStateByCode, getCountryByCode } from '../utils/countries';
import { sendSubmissionConfirmationEmail, sendStatusUpdateEmail } from '../utils/submissionEmailService';


export const useSubmissions = () => {
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState<IDSubmission[]>([]);

  // Cache geolocation data to avoid repeated API calls for same IP
  const geolocationCache = useMemo(() => new Map<string, any>(), []);

  const createSubmission = useCallback(async (
    documents: Array<{
      type: string;
      frontFile: File | null | undefined;
      backFile: File | null | undefined;
    }>,
    personalInfo: PersonalInfo,
    paymentInfo: {
      paymentMethod: 'paypal';
      paypalName: string;
      paypalEmail: string;
    }
  ): Promise<string | null> => {
    setLoading(true);
    
  // Try to get client IP, source country, and full geolocation data (best-effort; non-blocking)
    // We'll use ip-api.com which provides comprehensive geolocation data in one request
    let ipAddress: string | null = null;
    let sourceCountry: string | null = null;
    let geolocationData: any = null;
    
    try {
      // Use ip-api.com for comprehensive geolocation data with caching
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000);
      
      // Try to get IP from a fast service first to use as cache key
      const ipResp = await fetch('https://api.ipify.org?format=json', { signal: controller.signal });
      const ipData = await ipResp.json();
      const currentIP = ipData.ip;
      
      // Check cache first
      if (geolocationCache.has(currentIP)) {
        const cachedData = geolocationCache.get(currentIP);
        ipAddress = cachedData.query || null;
        sourceCountry = cachedData.countryCode || cachedData.country || null;
        geolocationData = cachedData;
        // Using cached geolocation data for IP
      } else {
        // Fetch from a secure HTTPS geolocation provider (ipapi.co) if not cached
        // ipapi.co returns JSON like: { ip, city, region, country, country_name, country_code, latitude, longitude, ... }
        try {
          const ipForLookup = currentIP || '';
          const lookupUrl = ipForLookup ? `https://ipapi.co/${ipForLookup}/json/` : 'https://ipapi.co/json/';
          const geolocationResp = await fetch(lookupUrl, { signal: controller.signal });
          clearTimeout(timeout);

          if (geolocationResp.ok) {
            const apiData = await geolocationResp.json();

            // Normalize ipapi.co response into the shape the rest of the code expects
            const geoData: any = {
              status: 'success',
              query: apiData.ip || null,
              country: apiData.country_name || apiData.country || null,
              countryCode: apiData.country_code || apiData.country || null,
              regionName: apiData.region || apiData.region || null,
              city: apiData.city || null,
              lat: apiData.latitude || apiData.lat || null,
              lon: apiData.longitude || apiData.lon || null,
              // Keep the raw provider fields for debugging/inspection
              _raw: apiData,
            };

            ipAddress = geoData.query || null;
            sourceCountry = geoData.countryCode || geoData.country || null;
            geolocationData = geoData;

            // Cache the result for future use
            geolocationCache.set(currentIP, geoData);
          }
        } catch (err) {
          // If ipapi.co fails, gracefully continue without geolocation
          clearTimeout(timeout);
        }
      }
      clearTimeout(timeout);
    } catch (e) {
      // Geolocation detection failed
      // swallow any unexpected errors; submission continues without IP/geo info
      ipAddress = ipAddress || null;
      sourceCountry = sourceCountry || null;
      geolocationData = null;
    }

    if (!SUPABASE_CONFIGURED) {
      // Simulate submission without Supabase
      const demoSubmissionId = 'demo-submission-' + Date.now();
      
  // Removed the call to sendSubmissionConfirmationEmail here
      toast.success('Demo mode: Submission simulated successfully! (Supabase not configured)');
      setTimeout(() => setLoading(false), 2000); // Simulate upload time
      return demoSubmissionId;
    }

    try {
      // First, create the submission record with personal information
      // Build insert payload and only include ip fields when available.
      // Convert state code to readable name when possible to avoid storing unexpected numeric IDs
      const stateName = personalInfo.state ? (getStateByCode(personalInfo.country, personalInfo.state)?.name || personalInfo.state) : null;
      // Convert country code to full country name for storage
      const countryName = personalInfo.country ? (getCountryByCode(personalInfo.country)?.name || personalInfo.country) : null;

      const insertPayload: any = {
        full_name: personalInfo.fullName,
        email: personalInfo.email,
        country: countryName || personalInfo.country,
        state: stateName || null,
        city: personalInfo.city,
        address: personalInfo.address,
        postal_code: personalInfo.postalCode || null,
        payment_method: paymentInfo?.paymentMethod || null,
        paypal_name: paymentInfo?.paypalName || null,
        paypal_email: paymentInfo?.paypalEmail || null,
        status: 'pending',
        payment_amount: null, // to be set by admin later
        user_agent: navigator.userAgent,
        session_id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      };

      // Only include these keys if we actually resolved values to avoid
      // triggering PostgREST schema errors when the columns don't exist.
      if (ipAddress) insertPayload.ip_address = ipAddress;
      if (sourceCountry) insertPayload.source_country = sourceCountry;
      if (geolocationData) insertPayload.geolocation_data = geolocationData;


      const { data: submissionData, error: submissionError } = await supabase
        .from('submissions')
        .insert(insertPayload)
        .select()
        .single();

      if (submissionError) {
        console.error('Supabase submission error details:', submissionError);
        // Helpful developer hint for common schema mismatch (missing ip columns)
        try {
          const errObj = submissionError as any;
          if (errObj && errObj.code === 'PGRST204') {
            console.warn("Supabase schema cache error (PGRST204). This often means a column included in the insert payload doesn't exist in the database schema. If you intend to store client IPs, add 'ip_address' and/or 'source_country' columns to the 'submissions' table, then refresh the PostgREST / Supabase schema cache. Alternatively the frontend will now omit unknown columns and continue.");
          }
        } catch (e) {
          // ignore
        }
        throw submissionError;
      }

      // Now upload documents and create document records
      for (let i = 0; i < documents.length; i++) {
        const doc = documents[i];
        const documentData: any = {
          submission_id: submissionData.id,
          identity_type: doc.type
        };
        
        // Upload front file if exists
        if (doc.frontFile) {
          const frontFileName = `${submissionData.id}-${i}-front-${doc.frontFile.name}`;
          const frontUpload = await supabase.storage
            .from(STORAGE_BUCKET)
            .upload(frontFileName, doc.frontFile);

          if (frontUpload.error) {
            console.error('Front upload error:', frontUpload.error);
            throw new Error(`Failed to upload front image: ${frontUpload.error.message}`);
          }

          const { data: frontUrl } = supabase.storage
            .from(STORAGE_BUCKET)
            .getPublicUrl(frontUpload.data.path);

          documentData.front_url = frontUrl.publicUrl;
          documentData.file_size_front = doc.frontFile.size;
          documentData.mime_type_front = doc.frontFile.type;
        }
        
        // Upload back file if exists
        if (doc.backFile) {
          const backFileName = `${submissionData.id}-${i}-back-${doc.backFile.name}`;
          const backUpload = await supabase.storage
            .from(STORAGE_BUCKET)
            .upload(backFileName, doc.backFile);

          if (backUpload.error) {
            console.error('Back upload error:', backUpload.error);
            throw new Error(`Failed to upload back image: ${backUpload.error.message}`);
          }

          const { data: backUrl } = supabase.storage
            .from(STORAGE_BUCKET)
            .getPublicUrl(backUpload.data.path);

          documentData.back_url = backUrl.publicUrl;
          documentData.file_size_back = doc.backFile.size;
          documentData.mime_type_back = doc.backFile.type;
        }

        // Create document record
        const { error: docError } = await supabase
          .from('submission_documents')
          .insert(documentData);

        if (docError) {
          console.error('Document creation error:', docError);
          throw docError;
        }
      }

      // Send confirmation email to the user
      try {
        // Create a minimal submission object for the email service
        const submissionForEmail: IDSubmission = {
          id: submissionData.id,
          personalInfo: {
            fullName: personalInfo.fullName,
            email: personalInfo.email,
            country: countryName || personalInfo.country,
            state: stateName || personalInfo.state,
            city: personalInfo.city,
            address: personalInfo.address,
            postalCode: personalInfo.postalCode || undefined,
            emailVerified: true // Assuming verified since they submitted
          },
          documents: documents.map(doc => ({
            type: doc.type as 'passport' | 'driver_license' | 'national_id',
            frontImage: doc.frontFile ? { file: doc.frontFile, preview: '', progress: 100, uploaded: true, url: '' } : null,
            backImage: doc.backFile ? { file: doc.backFile, preview: '', progress: 100, uploaded: true, url: '' } : null
          })),
          status: 'pending',
          payment_amount: 0, // Will be set by admin later
          created_at: submissionData.created_at || new Date().toISOString(),
          updated_at: submissionData.updated_at || new Date().toISOString()
        };

        await sendSubmissionConfirmationEmail(submissionForEmail);
        // Confirmation email sent successfully
      } catch (emailError) {
        console.error('❌ Failed to send confirmation email:', emailError);
        // Don't fail the submission if email fails
      }
      
      // Success - don't show toast here, let the component handle it
      return submissionData.id;
    } catch (error) {
      console.error('Submission error:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      toast.error('Failed to submit ID. Please try again.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [geolocationCache]);

  const getSubmissions = useCallback(async () => {
    setLoading(true);
    
    if (!SUPABASE_CONFIGURED) {
      // Provide demo data when Supabase is not configured
      const demoSubmissions: IDSubmission[] = [
        {
          id: 'demo-1',
          documents: [
            {
              type: 'passport',
              frontImage: { 
                file: null as any, 
                preview: '', 
                progress: 100, 
                uploaded: true, 
                url: 'https://via.placeholder.com/400x300?text=Demo+Front+Passport' 
              },
              backImage: { 
                file: null as any, 
                preview: '', 
                progress: 100, 
                uploaded: true, 
                url: 'https://via.placeholder.com/400x300?text=Demo+Back+Passport' 
              }
            }
          ],
          personalInfo: {
            fullName: 'John Doe',
            email: 'john.doe@example.com',
            country: 'US',
            state: 'CA',
            city: 'Los Angeles',
            address: '123 Demo Street',
            postalCode: '90210'
          },
          payment_method: 'paypal',
          paypal_name: 'John Doe Demo',
          paypal_email: 'demo@example.com',
          status: 'pending',
          ip_address: '127.0.0.1',
          source_country: 'Local',
          payment_amount: 5,
          created_at: new Date().toISOString(),
        },
        {
          id: 'demo-2',
          documents: [
            {
              type: 'driver_license',
              frontImage: { 
                file: null as any, 
                preview: '', 
                progress: 100, 
                uploaded: true, 
                url: 'https://via.placeholder.com/400x300?text=Demo+Front+License' 
              },
              backImage: { 
                file: null as any, 
                preview: '', 
                progress: 100, 
                uploaded: true, 
                url: 'https://via.placeholder.com/400x300?text=Demo+Back+License' 
              }
            }
          ],
          personalInfo: {
            fullName: 'Jane Smith',
            email: 'jane.smith@example.com',
            country: 'GB',
            state: 'England',
            city: 'London',
            address: '456 Example Road',
            postalCode: 'SW1A 1AA'
          },
          payment_method: 'paypal',
          paypal_name: 'Jane Smith Demo',
          paypal_email: 'jane.demo@example.com',
          status: 'approved_pending_payment',
          ip_address: '127.0.0.2',
          source_country: 'GB',
          payment_amount: 5,
          created_at: new Date(Date.now() - 86400000).toISOString(), // Yesterday
        },
        {
          id: 'demo-3',
          documents: [
            {
              type: 'national_id',
              frontImage: { 
                file: null as any, 
                preview: '', 
                progress: 100, 
                uploaded: true, 
                url: 'https://via.placeholder.com/400x300?text=Demo+Front+National' 
              },
              backImage: { 
                file: null as any, 
                preview: '', 
                progress: 100, 
                uploaded: true, 
                url: 'https://via.placeholder.com/400x300?text=Demo+Back+National' 
              }
            }
          ],
          personalInfo: {
            fullName: 'Alice Johnson',
            email: 'alice.johnson@example.com',
            country: 'CA',
            state: 'ON',
            city: 'Toronto',
            address: '789 Maple Avenue',
            postalCode: 'M5V 3A8'
          },
          payment_method: 'paypal',
          paypal_name: 'Alice Johnson Demo',
          paypal_email: 'alice.demo@example.com',
          status: 'rejected',
          ip_address: '127.0.0.3',
          source_country: 'CA',
          rejection_reason: 'Document unclear',
          payment_amount: 5,
          created_at: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
        },
        {
          id: 'demo-4',
          documents: [
            {
              type: 'passport',
              frontImage: { 
                file: null as any, 
                preview: '', 
                progress: 100, 
                uploaded: true, 
                url: 'https://via.placeholder.com/400x300?text=Demo+Front+Passport+2' 
              },
              backImage: { 
                file: null as any, 
                preview: '', 
                progress: 100, 
                uploaded: true, 
                url: 'https://via.placeholder.com/400x300?text=Demo+Back+Passport+2' 
              }
            },
            {
              type: 'driver_license',
              frontImage: { 
                file: null as any, 
                preview: '', 
                progress: 100, 
                uploaded: true, 
                url: 'https://via.placeholder.com/400x300?text=Demo+Front+License+2' 
              },
              backImage: { 
                file: null as any, 
                preview: '', 
                progress: 100, 
                uploaded: true, 
                url: 'https://via.placeholder.com/400x300?text=Demo+Back+License+2' 
              }
            }
          ],
          personalInfo: {
            fullName: 'Bob Wilson',
            email: 'bob.wilson@example.com',
            country: 'AU',
            state: 'NSW',
            city: 'Sydney',
            address: '321 Demo Boulevard',
            postalCode: '2000'
          },
          payment_method: 'paypal',
          paypal_name: 'Bob Wilson Demo',
          paypal_email: 'bob.wilson@example.com',
          status: 'paid',
          ip_address: '127.0.0.4',
          source_country: 'AU',
          payment_amount: 5,
          created_at: new Date(Date.now() - 259200000).toISOString(), // 3 days ago
        },
      ];
      
      setTimeout(() => {
        setSubmissions(demoSubmissions);
        setLoading(false);
      }, 1000); // Simulate network delay
      return;
    }

    try {
      // Fetch submissions with their documents
      const { data: submissionsData, error: submissionsError } = await supabase
        .from('submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (submissionsError) {
        throw submissionsError;
      }

      // Fetch all submission documents
      const { data: documentsData, error: documentsError } = await supabase
        .from('submission_documents')
        .select('*');

      if (documentsError) {
        throw documentsError;
      }

  // Combine submissions with their documents
      const submissionsWithDocuments: IDSubmission[] = (submissionsData || []).map(submission => {
        const submissionDocuments = (documentsData || [])
          .filter(doc => doc.submission_id === submission.id)
          .map(doc => ({
            type: doc.identity_type || doc.document_type || doc.type || 'document', // Use identity_type from DB
            frontImage: {
              file: null as any,
              preview: '',
              progress: 100,
              uploaded: true,
              url: doc.front_url || doc.front_image_url
            },
            backImage: {
              file: null as any,
              preview: '',
              progress: 100,
              uploaded: true,
              url: doc.back_url || doc.back_image_url
            }
          }));

        return {
          id: submission.id,
          documents: submissionDocuments,
          personalInfo: {
            fullName: submission.full_name,
            email: submission.email,
            country: submission.country,
            state: submission.state || '',
            city: submission.city,
            address: submission.address,
            postalCode: submission.postal_code || ''
          },
          payment_method: submission.payment_method,
          paypal_name: submission.paypal_name,
          paypal_email: submission.paypal_email,
          status: submission.status,
          rejection_reason: submission.rejection_reason,
          admin_notes: submission.admin_notes,
          reviewed_at: submission.reviewed_at,
          payment_processed_at: submission.payment_processed_at,
          payment_amount: submission.payment_amount,
          created_at: submission.created_at,
          updated_at: submission.updated_at,
          user_agent: submission.user_agent,
          session_id: submission.session_id,
          ip_address: submission.ip_address,
          source_country: submission.source_country,
          geolocation_data: submission.geolocation_data
        };
      });

      setSubmissions(submissionsWithDocuments);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSubmissionStatus = useCallback(async (
    id: string,
    status: 'rejected' | 'approved_pending_payment' | 'paid',
    rejectionReason?: string,
    paymentAmount?: number,
    adminNotes?: string
  ) => {
    if (!SUPABASE_CONFIGURED) {
      // Simulate status update in demo mode
      const now = new Date().toISOString();
      setSubmissions(prev => prev.map(submission => 
        submission.id === id 
          ? { 
              ...submission, 
              status, 
              rejection_reason: rejectionReason || submission.rejection_reason,
              payment_amount: paymentAmount !== undefined ? paymentAmount : submission.payment_amount,
              admin_notes: adminNotes !== undefined ? adminNotes : submission.admin_notes,
              // set reviewed_at when approving/rejecting
              reviewed_at: (status === 'rejected' || status === 'approved_pending_payment') ? now : submission.reviewed_at,
              // set payment_processed_at when marking paid
              payment_processed_at: status === 'paid' ? now : submission.payment_processed_at,
              updated_at: now
            }
          : submission
      ));
      toast.success(`Demo mode: Submission ${status.replace(/_/g, ' ')} successfully!`);
      return;
    }

    try {
      const updateData: any = {
        status,
        rejection_reason: rejectionReason || null,
      };

      if (paymentAmount !== undefined) {
        updateData.payment_amount = paymentAmount;
      }

      if (adminNotes !== undefined) {
        updateData.admin_notes = adminNotes;
      }

      const now = new Date().toISOString();
      // If approving or rejecting, set reviewed_at; if marking paid, set payment_processed_at
      if (status === 'rejected' || status === 'approved_pending_payment') {
        updateData.reviewed_at = now;
      }

      if (status === 'paid') {
        updateData.payment_processed_at = now;
      }

      // Always update updated_at
      updateData.updated_at = now;

      const { error } = await supabase
        .from('submissions')
        .update(updateData)
        .eq('id', id);

      if (error) {
        throw error;
      }

      // Send status update email to the user
      try {
        // Find the submission to get the full details for email
        const submission = submissions.find(s => s.id === id);
        if (submission) {
          const additionalInfo: any = {};
          if (rejectionReason) {
            additionalInfo.rejectionReason = rejectionReason;
          }
          if (paymentAmount !== undefined) {
            additionalInfo.paymentAmount = paymentAmount;
          }

          await sendStatusUpdateEmail(submission, status, additionalInfo);
          // Status update email sent
        }
      } catch (emailError) {
        console.error('❌ Failed to send status update email:', emailError);
        // Don't fail the status update if email fails
      }

      // Success - don't show toast here, let the component handle it
      await getSubmissions(); // Refresh the list
    } catch (error) {
      console.error('Error updating submission:', error);
      throw error; // Let the component handle the error toast
    }
  }, [getSubmissions, submissions]);

  const approveSubmission = useCallback(async (id: string, paymentAmount: number, adminNotes?: string) => {
    return updateSubmissionStatus(id, 'approved_pending_payment', undefined, paymentAmount, adminNotes);
  }, [updateSubmissionStatus]);

  const rejectSubmission = useCallback(async (id: string, rejectionReason: string, adminNotes?: string) => {
    return updateSubmissionStatus(id, 'rejected', rejectionReason, undefined, adminNotes);
  }, [updateSubmissionStatus]);

  return {
    loading,
    submissions,
    createSubmission,
    getSubmissions,
    updateSubmissionStatus,
    approveSubmission,
    rejectSubmission,
  };
};